import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Select from "react-select";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Grid,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { baseURL } from "../../Restclient/datacollection";
import { ThemeContext } from "../Homepage";
import LK_Delete from "../../images/LK_Delete";
import LK_Edit from "../../images/LK_Edit";
import { set } from "date-fns";
import CustomDialogBox from "../CommonComponents/CustomDialogBox";

function Notification() {
  const [userGroups, setUserGroups] = useState([]);
  const themechange = useSelector((state) => state.themedata.theme);
  const [ruleName, setRuleName] = useState("");
  const [view, setView] = useState("list");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUserGroup, setSelectedUserGroup] = useState([]);
  const [isScheduleSaved, setIsScheduleSaved] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [openDialog, setOpenDialog] = useState(0);
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogType, setDialogType] = useState("Info");
  const [condition, setCondition] = useState(0);
  const [groupToDelete, setGrouptoDelete] = useState(false);
  const projectname = useSelector(
    (state) => state.mainpagedata.selectedprojectname
  );
  const [schedulerConfig, setSchedulerConfig] = useState({
    repeatOption: "Does not repeat",
    days: [],
    months: [],
    timeRanges: [{ from: "", to: "" }],
    scheduleAt: null,
    startDate: null,
    endDate: null,
    frequency: null,
  });

  const handleCloseDialog = () => {
    setOpenDialog(0);
    setCondition(0);
  };

  const handleConfirmDialog = () => {
    setOpenDialog(0);
    setCondition(0);
  };

  const { theme } = useContext(ThemeContext);
  const userdetails = useSelector((state) => state.logindata.userdetails);

  useEffect(() => {
    if (view === "list") {
      fetchUserGroups();
    }
  }, [view]);

  useEffect(() => {
    fetchUserGroups();
  }, [projectname]);

  useEffect(() => {
    setFilteredUsers(users);
  }, [users]);

  // Sort by reverse array index - newest rules last in API, so reverse to get newest first
  const sortRulesNewestFirst = (groups) => {
    // Reverse the array to put newest (last) first
    const sorted = [...groups].reverse();
    return sorted;
  };

  const fetchUserGroups = () => {
    axios
      .get(baseURL + "/api/fetchUserGroups")
      .then((response) => {
        // REVERSE the array - API returns oldest first, we need newest first
        const sortedGroups = sortRulesNewestFirst(response.data);
        setUserGroups(sortedGroups);
      })
      .catch((error) => console.error("Error fetching user groups:", error));
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(baseURL + "/api/fetchUsers");

      const normalizedUsers = response.data.formattedUser.map((user) => ({
        userID: user.userID || user.userid || user.id || "",
        userName: user.userName || user.username || user.UserName || "",
        email: user.email || user.Email || "",
        ...user,
      }));

      setUsers(normalizedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
      setDialogMessage("Error fetching users. Please try again.");
      setDialogType("Error");
      setOpenDialog(1);
    }
  };

  const checkDuplicateRule = () => {
    const trimmedRuleName = (ruleName || "").trim().toLowerCase();

    if (!trimmedRuleName) {
      return false;
    }

    for (let i = 0; i < userGroups.length; i++) {
      const group = userGroups[i];

      if (editingGroup && group.id === editingGroup.id) {
        continue;
      }

      const groupName = (group.groupName || "").trim().toLowerCase();
      if (groupName === trimmedRuleName) {
        return true;
      }
    }

    return false;
  };

  const handleSaveGroup = () => {
    if (!ruleName || ruleName.trim() === "") {
      setDialogMessage("Please enter a rule name.");
      setDialogType("Error");
      setOpenDialog(1);
      return;
    }

    if (selectedUserGroup.length === 0) {
      setDialogMessage("Please select at least one user.");
      setDialogType("Error");
      setOpenDialog(1);
      return;
    }

    if (checkDuplicateRule()) {
      setDialogMessage("A rule with this name already exists. Please choose a different name.");
      setDialogType("Error");
      setOpenDialog(1);
      return;
    }

    const payload = {
      id: editingGroup?.id || null,
      groupName: ruleName,
      ProjectID: userdetails.Projectid,
      organizationId: userdetails.organizationId,
      users: selectedUserGroup,
      repeatOption: schedulerConfig.repeatOption,
      timestamp: schedulerConfig.timestamp,
      timeRanges: schedulerConfig.useTimeRange ? schedulerConfig.timeRanges : [],
      days: schedulerConfig.days,
      months: schedulerConfig.months,
      frequency: schedulerConfig.frequency,
      startDate: schedulerConfig.startDate,
      endDate: schedulerConfig.endDate,
    };

    const requestMethod = editingGroup ? "put" : "post";
    const requestURL = editingGroup
      ? `${baseURL}/api/updateNotificationRule`
      : `${baseURL}/api/saveNotificationRule`;

    axios[requestMethod](requestURL, payload)
      .then((response) => {
        // Fetch fresh rules - REVERSE to newest first
        axios.get(baseURL + "/api/fetchUserGroups")
          .then((groupResponse) => {
            const sortedGroups = sortRulesNewestFirst(groupResponse.data);
            setUserGroups(sortedGroups);
            
            setDialogMessage(
              editingGroup
                ? "Notification rule updated successfully!"
                : "Notification rule saved successfully!"
            );

            setIsScheduleSaved(false);
            setDialogType("Success");
            setEditingGroup(null);
            setIsEditMode(false);
            setCondition(1);
            setView("list");
          });
      })
      .catch((error) => {
        console.error("Error saving notification rule:", error);
        setDialogMessage("Failed to save notification rule. Please try again.");
        setDialogType("Error");
        setOpenDialog(1);
      });
  };

  useEffect(() => {
    if (condition === 1) {
      setOpenDialog(1);
    }
  }, [condition]);

  const handleConfirmDelete = () => {
    if (!groupToDelete) return;
    axios
      .delete(`${baseURL}/api/deleteUserGroup/${groupToDelete.id}`)
      .then(() => {
        setDialogType("Success");
        setDialogMessage("User group deleted successfully.");
        fetchUserGroups();
      })
      .catch((error) => {
        console.error("Error deleting group:", error);
        setDialogType("Error");
        setDialogMessage("Failed to delete user group. Please try again.");
      })
      .finally(() => {
        setOpenDialog(1);
        setGrouptoDelete(null);
      });
  };

  const handleEditGroup = (groupId) => {
    const groupToEdit = userGroups.find((group) => group.id === groupId);

    if (groupToEdit) {
      setEditingGroup(groupToEdit);
      setIsEditMode(true);
      setRuleName(groupToEdit.groupName);
      setSelectedUserGroup(groupToEdit.users || []);
      setSchedulerConfig({
        repeatOption: groupToEdit.repeatOption || "Does not repeat",
        timestamp: groupToEdit.timestamp,
        timeRanges: groupToEdit.timeRanges || [{ from: "", to: "" }],
        days: groupToEdit.days || [],
        months: groupToEdit.months || [],
        frequency: groupToEdit.frequency,
        startDate: groupToEdit.startDate,
        endDate: groupToEdit.endDate,
      });
      setIsScheduleSaved(true);
      setView("create");
    }
  };

  const handleSearchUsers = (e) => {
    const searchValue = (e.target.value || "").toLowerCase();

    if (searchValue.trim() === "") {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter((user) => {
        const userName = (user.userName || "").toLowerCase();
        const email = (user.email || "").toLowerCase();
        return userName.includes(searchValue) || email.includes(searchValue);
      });
      setFilteredUsers(filtered);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        margin: "0 auto",
        padding: "20px",
        boxSizing: "border-box",
        backgroundColor: "var(--SecondaryBackground)",
        color: "#000000",
        fontFamily: "Roboto, sans-serif",
      }}
    >
      {view === "list" && (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <Typography variant="h5" color="#008cd7">
              Configured Notification Rule
            </Typography>
            <Tooltip title="Create a new notification rule" arrow>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  setIsScheduleSaved(true);
                  setRuleName("");
                  setSelectedUserGroup([]);
                  setEditingGroup(null);
                  setView("create");
                  fetchUsers();
                }}
                sx={{
                  padding: "8px 20px",
                  backgroundColor: "var(--LK_BrandBlue)",
                  color: "var(--White)",
                  border: "none",
                  borderRadius: "20px",
                  fontSize: "16px",
                  fontWeight: 700,
                  fontFamily: "Roboto",
                  cursor: "pointer",
                  marginLeft: "10px",
                }}
              >
                Add Rule
              </Button>
            </Tooltip>
          </Box>

          {!isScheduleSaved && (
            <Box
              sx={{
                width: "100%",
                padding: "8px",
                borderRadius: "10px",
                backgroundColor: "var(--SecondaryBackground)",
              }}
            >
              {userGroups.length === 0 ? (
                <Typography>No user groups found. Please create a new group.</Typography>
              ) : (
                <Table
                  sx={{
                    width: "100%",
                    marginTop: "20px",
                    backgroundColor: "var(--SecondaryBackground)",
                    color: theme === "dark" ? "#ffffff" : "#000000",
                  }}
                >
                  <TableHead sx={{ backgroundColor: "none" }}>
                    <TableRow sx={{ backgroundColor: "none" }}>
                      <TableCell sx={{ color: theme === "dark" ? "#ffffff" : "#000000", textAlign: "center" }}>
                        Rule Name
                      </TableCell>
                      <TableCell sx={{ color: theme === "dark" ? "#ffffff" : "#000000", textAlign: "center" }}>
                        Edit
                      </TableCell>
                      <TableCell sx={{ color: theme === "dark" ? "#ffffff" : "#000000", textAlign: "center" }}>
                        Delete
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userGroups.map((group) => (
                      <TableRow key={group.id}>
                        <TableCell sx={{ textAlign: "center", color: theme === "dark" ? "#ffffff" : "#000000" }}>
                          {group.groupName}
                        </TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                          <Tooltip title="Edit this notification rule" arrow>
                            <IconButton color="primary" onClick={() => handleEditGroup(group.id)}>
                              <LK_Edit color={"#005C8E"} width={"20px"} height={"20px"} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                          <Tooltip title="Delete this notification rule" arrow>
                            <IconButton
                              color="error"
                              onClick={() => {
                                setGrouptoDelete(group);
                                setDialogMessage("Are you sure you want to delete this user group?");
                                setOpenDialog(2);
                              }}
                            >
                              <LK_Delete color={"#005C8E"} width={"20px"} height={"20px"} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          )}
        </>
      )}

      {isScheduleSaved && (
        <Grid container spacing={20}>
          <Grid item xs={6}>
            <Box sx={{ marginBottom: "20px" }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Enter Rule Name
              </Typography>
              <TextField
                variant="standard"
                value={ruleName}
                onChange={(e) => setRuleName(e.target.value)}
                fullWidth
              />
            </Box>

            <Box sx={{ marginBottom: "20px" }}>
              <Box sx={{ marginBottom: "10px" }}>
                <Typography variant="h6" color="primary">
                  Select Usernames
                </Typography>
                <Typography variant="caption" sx={{ color: "gray" }}>
                  Search and select users below
                </Typography>
              </Box>

              <TextField
                label="Search Users"
                variant="standard"
                onChange={handleSearchUsers}
                placeholder="Search by username or email"
                fullWidth
              />
              <Table sx={{ width: "100%" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: theme === "dark" ? "#ffffff" : "#000000" }}>Select</TableCell>
                    <TableCell sx={{ color: theme === "dark" ? "#ffffff" : "#000000" }}>S.No.</TableCell>
                    <TableCell sx={{ color: theme === "dark" ? "#ffffff" : "#000000" }}>Usernames</TableCell>
                    <TableCell sx={{ color: theme === "dark" ? "#ffffff" : "#000000" }}>Email</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ color: theme === "dark" ? "#ffffff" : "#000000" }}>
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((user, index) => (
                      <TableRow key={user.userID}>
                        <TableCell sx={{ color: theme === "dark" ? "#ffffff" : "#000000" }}>
                          <input
                            type="checkbox"
                            checked={selectedUserGroup.some((selectedUser) => selectedUser.Userid === user.userID)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUserGroup((prev) => [
                                  ...prev,
                                  { Userid: user.userID, Email: user.email, UserName: user.userName },
                                ]);
                              } else {
                                setSelectedUserGroup((prev) =>
                                  prev.filter((selectedUser) => selectedUser.Userid !== user.userID)
                                );
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: theme === "dark" ? "#ffffff" : "#000000" }}>{index + 1}</TableCell>
                        <TableCell sx={{ color: theme === "dark" ? "#ffffff" : "#000000" }}>
                          {user.userName || "N/A"}
                        </TableCell>
                        <TableCell sx={{ color: theme === "dark" ? "#ffffff" : "#000000" }}>
                          {user.email || "N/A"}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: "center" }}>
                        No users found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "20px", marginTop: "30px" }}>
              <Tooltip title="Save the notification rule" arrow>
                <Button variant="contained" color="primary" onClick={handleSaveGroup} sx={{ borderRadius: "20px", backgroundColor: "#005c8e" }}>
                  Save Rule
                </Button>
              </Tooltip>

              <Tooltip title="Cancel and return to list" arrow>
                <Button
                  variant="contained"
                  sx={{ backgroundColor: "grey", borderRadius: "20px" }}
                  onClick={() => {
                    setRuleName("");
                    setSelectedUserGroup([]);
                    setIsScheduleSaved(false);
                    setEditingGroup(null);
                    setView("list");
                  }}
                >
                  Cancel
                </Button>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      )}

      <CustomDialogBox
        dialogTitle={"confirmation"}
        dialogText={dialogMessage}
        openDialog={openDialog === 2}
        handleCloseDialog={() => setOpenDialog(false)}
        handleConfirmDialog={handleConfirmDelete}
        confirmText={"OK"}
        cancelText={"Cancel"}
        type={dialogType}
      />
      <CustomDialogBox
        dialogTitle={dialogType === "Success" ? "Success" : "Error"}
        dialogText={dialogMessage}
        openDialog={openDialog === 1}
        handleCloseDialog={handleCloseDialog}
        handleConfirmDialog={handleConfirmDialog}
        confirmText={"OK"}
        cancelText={"Cancel"}
        type={dialogType}
      />
    </Box>
  );
}

export default Notification;
